import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updatePlanPrice = (plan, newPrice) => {
    setSettings({
      ...settings,
      subscriptionPlans: {
        ...settings.subscriptionPlans,
        [plan]: {
          ...settings.subscriptionPlans[plan],
          price: parseFloat(newPrice)
        }
      }
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Subscription Plans */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <span>💳</span> Subscription Plans
          </h3>
          
          <div className="space-y-4">
            {settings?.subscriptionPlans && Object.entries(settings.subscriptionPlans).map(([key, plan]) => (
              <div key={key} className="border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Plan Name</p>
                    <p className="font-medium">{plan.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1 block">Price (R)</label>
                    <input
                      type="number"
                      value={plan.price}
                      onChange={(e) => updatePlanPrice(key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Property Limit</p>
                    <p className="font-medium">{plan.limit} properties</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trial Settings */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <span>⏰</span> Trial Period Settings
          </h3>
          
          <div className="max-w-md">
            <label className="text-sm text-gray-600 mb-2 block">Trial Duration (days)</label>
            <input
              type="number"
              value={settings?.trialDuration || 90}
              onChange={(e) => setSettings({ ...settings, trialDuration: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-2">Default: 90 days (3 months)</p>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <span>⚙️</span> Feature Toggles
          </h3>
          
          <div className="space-y-4">
            {settings?.featuresEnabled && Object.entries(settings.featuresEnabled).map(([key, enabled]) => (
              <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                  <p className="text-sm text-gray-500">Enable or disable this feature platform-wide</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setSettings({
                      ...settings,
                      featuresEnabled: {
                        ...settings.featuresEnabled,
                        [key]: e.target.checked
                      }
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Note:</strong> Changes to subscription prices will only affect new subscriptions. Existing subscriptions will continue at their current price.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}